"use client";

// ============================================================================
// /500 — Unerwarteter Fehler (Client-Boundary)
// ----------------------------------------------------------------------------
// Next.js App-Router-Konvention: error.tsx fängt unbehandelte Errors innerhalb
// des umgebenden Layouts ab. Layout selbst bleibt sichtbar (Header/Footer).
//
// Sentry-Integration: Error wird automatisch erfasst. Der globale PII-Scrubber
// in lib/observability/scrubber.ts redaktiert Emails/IBANs/JWTs/Tokens, bevor
// das Event ans Sentry-EU-Projekt geht. Doppelter Schutz: useEffect ruft
// captureException explizit, falls Next.js' Auto-Capture eine Race verliert.
// ============================================================================

import { useEffect } from "react";
import { LogoSignet } from "@/components/brand/logo-signet";
import Link from "next/link";
import { AlertTriangle, Lock, RefreshCw, Server } from "lucide-react";
import * as Sentry from "@sentry/nextjs";

export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error, {
      tags: { boundary: "app/error" },
      extra: { digest: error.digest },
    });
  }, [error]);

  return (
    <div className="theme-v3 min-h-screen surface-0 text-fg-primary relative flex flex-col overflow-hidden selection:bg-[var(--color-accent-500)]/25">
      <div className="absolute inset-0 bg-grid-lines mask-radial opacity-40 pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-[var(--color-accent-500)]/[0.06] to-transparent pointer-events-none" aria-hidden="true" />

      <main
        id="main-content"
        tabIndex={-1}
        className="flex-1 flex flex-col items-center justify-center px-6 py-24 relative z-10 outline-none"
      >
        <div className="w-full max-w-lg space-y-10">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center justify-center gap-2.5 group"
            aria-label="SiFa Flow Startseite"
          >
            <LogoSignet size={36} className="shrink-0" />
            <span className="heading-industrial text-base font-semibold tracking-tight">
              SiFa<span className="text-fg-muted">Flow</span>
            </span>
          </Link>

          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-2">
            <span className="font-mono text-fs-micro text-fg-faint tabular-nums">
              500
            </span>
            <span className="eyebrow">Unerwarteter Fehler</span>
          </div>

          {/* Heading + Body */}
          <div className="text-center space-y-4">
            <h1 className="heading-industrial text-3xl sm:text-4xl font-semibold leading-[1.08]">
              Hier ist gerade
              <br />
              <span className="text-fg-secondary">etwas schiefgelaufen.</span>
            </h1>
            <p className="text-fg-secondary leading-relaxed max-w-sm mx-auto text-fs-lg">
              Wir haben den Fehler automatisch erfasst und kümmern uns darum.
              Sie können den letzten Schritt nochmal versuchen oder auf die
              Startseite zurückkehren.
            </p>
          </div>

          {/* Status Panel */}
          <div className="panel-elevated p-5 space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-[var(--color-signal-amber)]" />
              <span className="font-mono text-fs-xs font-semibold uppercase tracking-[0.12em] text-fg-secondary">
                Was tun?
              </span>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={reset}
                className="btn-primary-industrial inline-flex w-full items-center justify-center gap-2 px-4 h-11 rounded-md text-fs-md font-semibold"
              >
                <RefreshCw className="h-4 w-4" />
                Erneut versuchen
              </button>
              <Link
                href="/"
                className="hairline bg-[var(--overlay-1)] hover:bg-[var(--overlay-2)] text-fg-primary inline-flex w-full items-center justify-center gap-2 px-4 h-11 rounded-md text-fs-md font-semibold transition-colors"
              >
                Zur Startseite
              </Link>
            </div>

            {error.digest && (
              <div className="hairline-t pt-3 text-[10.5px] font-mono text-fg-faint tabular-nums text-center">
                Referenz-ID:{" "}
                <span className="text-fg-muted select-all">{error.digest}</span>
              </div>
            )}
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-5 text-xs text-fg-muted">
            <span className="inline-flex items-center gap-1.5">
              <Server className="h-3 w-3 text-[var(--color-accent-500)]" />
              Hosting Frankfurt
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3 w-3 text-[var(--color-accent-500)]" />
              DSGVO Art. 9
            </span>
          </div>
        </div>
      </main>

      <footer className="hairline-t py-6 px-6 relative z-10">
        <div className="max-w-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-fs-xs font-mono text-fg-faint">
          <p className="tabular-nums">
            © {new Date().getFullYear()} SiFaFlow UG (haftungsbeschränkt) i. G.
          </p>
          <div className="flex items-center gap-4">
            <Link href="/impressum" className="hover:text-fg-primary transition-colors">
              Impressum
            </Link>
            <Link href="/datenschutz" className="hover:text-fg-primary transition-colors">
              Datenschutz
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
